import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  callCenterReportApi,
  type CallCenterInbox,
  type CallCenterManagerDashboard,
} from '../api/callCenterReport'
import { LeadBulkActionsBar } from '../components/LeadBulkActionsBar'
import {
  Button, Callout, Card, CardContent, CardHeader, Checkbox, DataTable, EmptyState, FilterBar, LoadingState, Page, PageHeader,
  Select, StatGrid, StatusPill,
} from '../components/ui'

// F-084 (REQ-112-001, REQ-065-001): cruscotto del responsabile del call center. Sola lettura (nessun importo);
// l'assegnazione dei "Nuovi lead" passa dalla barra delle operazioni multiple gia' esistente (F-066).
// Nessuna riassegnazione automatica e nessuna presa in carico: la coda e' comune.

const PERIODS: { days: number; label: string }[] = [
  { days: 1, label: 'Oggi' },
  { days: 7, label: 'Ultimi 7 giorni' },
  { days: 31, label: 'Ultimi 31 giorni' },
]

function pct(v: number | null): string {
  return v === null ? '—' : `${(v * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%`
}

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function fmtWait(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  return h < 48 ? `${h} h` : `${Math.floor(h / 24)} g`
}

function Kpi({ label, value, hint, tone }: { label: string; value: string | number; hint?: string | undefined; tone?: string | undefined }) {
  return (
    <Card variant="filled">
      <div className="md-typescale-body-small text-muted">{label}</div>
      <div className={`md-typescale-headline-small md-num${tone ? ` text-${tone}` : ''}`}>{value}</div>
      {hint && <div className="md-typescale-body-small text-muted">{hint}</div>}
    </Card>
  )
}

export function CallCenterManagerPage() {
  const [days, setDays] = useState(1)
  const [dash, setDash] = useState<CallCenterManagerDashboard | null>(null)
  const [inbox, setInbox] = useState<CallCenterInbox | null>(null)
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [d, i] = await Promise.all([callCenterReportApi.manager(days), callCenterReportApi.inbox(page)])
      setDash(d)
      setInbox(i)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento del cruscotto')
    } finally {
      setLoading(false)
    }
  }, [days, page])

  useEffect(() => {
    void load()
  }, [load])

  function toggle(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const items = inbox?.items ?? []
  const allOnPage = items.length > 0 && items.every(i => selected.has(i.id))
  const pages = inbox ? Math.max(1, Math.ceil(inbox.total / inbox.pageSize)) : 1

  return (
    <Page>
      <PageHeader
        title="Cruscotto call center"
        icon="support_agent"
        actions={
          <Button type="button" variant="outlined" onClick={() => void load()} disabled={loading}>
            Aggiorna
          </Button>
        }
      />

      <FilterBar>
        <Select
          id="cc-days"
          label="Periodo"
          value={days}
          onChange={e => setDays(Number(e.target.value))}
        >
          {PERIODS.map(p => <option key={p.days} value={p.days}>{p.label}</option>)}
        </Select>
      </FilterBar>

      {error && (
        <Callout
          tone="error"
          role="alert"
          action={<Button type="button" variant="text" onClick={() => void load()}>Riprova</Button>}
        >
          {error}
        </Callout>
      )}

      <div aria-busy={loading}>
        {loading && !dash ? (
          <LoadingState label="Caricamento..." />
        ) : dash && (
          <>
            <h2 className="md-typescale-title-small text-muted mb-2">Adesso</h2>
            <StatGrid className="mb-3">
              <Kpi label="Lead nuovi" value={dash.newLeads} />
              <Kpi label="Non assegnati" value={dash.unassignedLeads} tone={dash.unassignedLeads > 0 ? 'warning' : undefined} />
              <Kpi
                label="Fuori SLA"
                value={dash.outOfSlaLeads}
                tone={dash.outOfSlaLeads > 0 ? 'danger' : undefined}
                hint={dash.provisional.speedToLeadTargets ? 'Target per fonte provvisori' : undefined}
              />
              <Kpi label="Non lavorati" value={dash.notWorkedLeads} hint="Assegnati, senza alcun tentativo" />
              <Kpi label="Richiamate scadute" value={dash.overdueCallbacks} tone={dash.overdueCallbacks > 0 ? 'danger' : undefined} />
              {dash.presenceEnabled && dash.onlineOperators != null && (
                <Kpi
                  label="Operatori online"
                  value={dash.onlineOperators}
                  hint={`Con una richiesta negli ultimi ${dash.onlineWindowMinutes ?? 5} min`}
                />
              )}
              <Kpi
                label="Operatori attivi"
                value={dash.activeOperators}
                hint={dash.presenceEnabled
                  ? `Con una chiamata negli ultimi ${dash.provisional.activeMinutes} min (proxy secondario)`
                  : `Con una chiamata negli ultimi ${dash.provisional.activeMinutes} min (valore provvisorio: non esiste la presenza online)`}
              />
            </StatGrid>

            {dash.presenceEnabled && dash.onlineOperatorList != null && (
              <Card variant="outlined" padding="none" className="mb-3">
                <CardHeader title="Operatori online" />
                {dash.onlineOperatorList.length === 0 ? (
                  <CardContent className="text-muted md-typescale-body-small">Nessun operatore online in questo momento.</CardContent>
                ) : (
                  <CardContent>
                    <ul className="list-unstyled mb-0">
                      {dash.onlineOperatorList.map(o => (
                        <li key={o.userId} className="d-flex justify-content-between py-1">
                          <span>{o.name}</span>
                          <span className="text-muted md-typescale-body-small">
                            {o.minutesAgo < 1 ? 'visto ora' : `visto ${o.minutesAgo} min fa`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                )}
              </Card>
            )}

            <h2 className="md-typescale-title-small text-muted mb-2">
              {PERIODS.find(p => p.days === dash.period.days)?.label ?? `Ultimi ${dash.period.days} giorni`}
            </h2>
            <StatGrid className="mb-4">
              <Kpi label="Lead lavorati" value={dash.leadsWorked} />
              <Kpi label="Appuntamenti fissati" value={dash.appointmentsSet} hint={`Su lead lavorati: ${pct(dash.appointmentRate)}`} />
              <Kpi label="No show" value={dash.appointmentsNoShow} hint={`Sui fissati: ${pct(dash.noShowRate)}`} />
              <Kpi label="Appuntamenti conclusi" value={dash.appointmentsCompleted} hint={`Sui fissati: ${pct(dash.completedOnSet)}`} />
              <Kpi label="Lead convertiti" value={dash.leadsConvertedOnWorked} hint={`Sui lavorati: ${pct(dash.convertedOnWorked)}`} />
            </StatGrid>
          </>
        )}

        <LeadBulkActionsBar
          selectedIds={[...selected]}
          onClear={() => setSelected(new Set())}
          onApplied={() => { setSelected(new Set()); void load() }}
        />

        <Card variant="outlined" padding="none" className="mb-3">
          <CardHeader
            title="Nuovi lead da assegnare"
            actions={
              <span className="d-flex align-items-center gap-3">
                <Link to="/leads/inbox" className="md-typescale-body-small text-decoration-none">Tutti i nuovi lead</Link>
                {inbox && <span className="text-muted md-typescale-body-small" aria-live="polite">{inbox.total} lead</span>}
              </span>
            }
          />
          {inbox && items.length === 0 ? (
            <EmptyState icon="inbox" title="Nessun lead nuovo in attesa di assegnazione." compact />
          ) : (
            <DataTable variant="plain" caption="Lead nuovi non assegnati, dal piu' vecchio">
              <thead>
                <tr>
                  <th scope="col">
                    <Checkbox
                      label={<span className="md-sr-only">Seleziona tutti i lead della pagina</span>}
                      checked={allOnPage}
                      onChange={e => setSelected(prev => {
                        const next = new Set(prev)
                        for (const i of items) { if (e.target.checked) next.add(i.id); else next.delete(i.id) }
                        return next
                      })}
                    />
                  </th>
                  <th scope="col">Lead</th>
                  <th scope="col">Telefono</th>
                  <th scope="col">Fonte</th>
                  <th scope="col">Campagna</th>
                  <th scope="col" className="md-num">Punteggio</th>
                  <th scope="col">In attesa</th>
                  <th scope="col">Ingresso</th>
                </tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.id}>
                    <td>
                      <Checkbox
                        label={<span className="md-sr-only">{`Seleziona ${i.name || `lead ${i.id}`}`}</span>}
                        checked={selected.has(i.id)}
                        onChange={() => toggle(i.id)}
                      />
                    </td>
                    <td><Link to={`/leads/${i.id}`} className="text-decoration-none md-typescale-emphasized-body-medium">{i.name || `Lead #${i.id}`}</Link></td>
                    <td>{i.phone ?? '—'}</td>
                    <td>{i.sourceLabel ?? '—'}</td>
                    <td>{i.campaign ?? '—'}</td>
                    <td className="md-num">{i.score ?? '—'}</td>
                    <td className="text-nowrap">
                      {fmtWait(i.waitingMinutes)}
                      {i.outOfSla && <StatusPill tone="error" className="ms-1">Fuori SLA</StatusPill>}
                    </td>
                    <td className="text-nowrap">{fmtDateTime(i.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
          {inbox && pages > 1 && (
            <CardContent className="d-flex justify-content-between align-items-center">
              <Button type="button" variant="outlined" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                Precedente
              </Button>
              <span className="md-typescale-body-small text-muted">Pagina {page} di {pages}</span>
              <Button type="button" variant="outlined" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                Successiva
              </Button>
            </CardContent>
          )}
        </Card>

        {dash && (
          <details className="md-typescale-body-small text-muted mb-3">
            <summary>Come sono calcolati i numeri</summary>
            <ul className="mt-2">
              {dash.definitions.map(d => <li key={d}>{d}</li>)}
            </ul>
          </details>
        )}
      </div>
    </Page>
  )
}
